import { useState, useCallback } from 'react';
import { asciiStyles } from '../constants/asciiStyles';

/**
 * useASCIISettings Hook
 *
 * Manages all ASCII rendering settings in a centralized way.
 * Consolidates related useState hooks into a single cohesive interface.
 */
export const useASCIISettings = () => {
  // Core rendering settings
  const [density, setDensity] = useState(1.0);
  const [animationSpeed, setAnimationSpeed] = useState(8);
  const [columns, setColumns] = useState(160);
  const [gamma, setGamma] = useState(1.0);
  const [contrast, setContrast] = useState(1.0);
  const [characterCount, setCharacterCount] = useState(9);
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [asciiStyle, setAsciiStyle] = useState(asciiStyles.classic);
  const [characterSet, setCharacterSet] = useState(asciiStyles.classic.characterSet);

  // Custom dimension settings
  const [dimensionMode, setDimensionMode] = useState('custom'); // 'aspectRatio' | 'custom'
  const [customWidth, setCustomWidth] = useState(1920);
  const [customHeight, setCustomHeight] = useState(1080);

  // GIF export color settings
  const [gifBackgroundColor, setGifBackgroundColor] = useState('#F7F7F7');
  const [gifForegroundColor, setGifForegroundColor] = useState('#171717');

  // Generic update function for any setting
  const updateSetting = useCallback((key, value) => {
    switch (key) {
      case 'density':
        setDensity(value);
        break;
      case 'animationSpeed':
        setAnimationSpeed(value);
        break;
      case 'columns':
        setColumns(value);
        break;
      case 'gamma':
        setGamma(value);
        break;
      case 'contrast':
        setContrast(value);
        break;
      case 'characterCount':
        // Validate: characterCount should not exceed characterSet length
        setCharacterCount(Math.min(value, characterSet.length));
        break;
      case 'aspectRatio':
        setAspectRatio(value);
        break;
      case 'characterSet':
        setCharacterSet(value);
        // Auto-adjust characterCount if it exceeds new set length
        if (characterCount > value.length) {
          setCharacterCount(value.length);
        }
        break;
      case 'asciiStyle':
        setAsciiStyle(value);
        // Update character set when style changes
        setCharacterSet(value.characterSet);
        setCharacterCount(Math.min(characterCount, value.characterSet.length));
        break;
      case 'gifBackgroundColor':
        setGifBackgroundColor(value);
        break;
      case 'gifForegroundColor':
        setGifForegroundColor(value);
        break;
      case 'dimensionMode':
        setDimensionMode(value);
        break;
      case 'customWidth':
        setCustomWidth(value);
        break;
      case 'customHeight':
        setCustomHeight(value);
        break;
      default:
        console.warn(`Unknown setting key: ${key}`);
    }
  }, [characterSet.length, characterCount]);

  // Reset to defaults
  const resetSettings = useCallback(() => {
    setDensity(1.0);
    setAnimationSpeed(8);
    setColumns(160);
    setGamma(1.0);
    setContrast(1.0);
    setCharacterCount(9);
    setAspectRatio('16:9');
    setAsciiStyle(asciiStyles.classic);
    setCharacterSet(asciiStyles.classic.characterSet);
    setGifBackgroundColor('#F7F7F7');
    setGifForegroundColor('#171717');
    setDimensionMode('custom');
    setCustomWidth(1920);
    setCustomHeight(1080);
  }, []);

  // Batch update multiple settings
  const updateSettings = useCallback((updates) => {
    Object.entries(updates).forEach(([key, value]) => {
      updateSetting(key, value);
    });
  }, [updateSetting]);

  return {
    // Settings values
    settings: {
      density,
      animationSpeed,
      columns,
      gamma,
      contrast,
      characterCount,
      aspectRatio,
      asciiStyle,
      characterSet,
      gifBackgroundColor,
      gifForegroundColor,
      dimensionMode,
      customWidth,
      customHeight
    },

    // Individual setters (for direct use)
    setDensity,
    setAnimationSpeed,
    setColumns,
    setGamma,
    setContrast,
    setCharacterCount,
    setAspectRatio,
    setAsciiStyle,
    setCharacterSet,
    setGifBackgroundColor,
    setGifForegroundColor,
    setDimensionMode,
    setCustomWidth,
    setCustomHeight,

    // Generic updater
    updateSetting,

    // Batch updater
    updateSettings,

    // Reset function
    resetSettings
  };
};
